import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, Building2, Users, Brain, TrendingUp, CheckCircle, Mail, ArrowRight, Linkedin } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../../components/seo/SEO';
import MatrixBackground from '../../components/ui/MatrixBackground';

export default function Empresas() {
    const navigate = useNavigate();

    // Scroll to top when page loads
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const consultoriaFeatures = [
        'Implementación de soluciones de IA',
        'Análisis de arquitectura de datos actual',
        'Tableros y dashboards personalizados',
        'Diseño de pipelines ETL/ELT escalables',
        'Optimización de almacenamiento y procesamiento',
        'Migración a cloud (AWS, Azure, GCP) en caso de ser necesario',
        'Estrategia de Data Governance y calidad',
        'Automatización de procesos de negocio',
    ];

    const capacitacionFeatures = [
        'Programas personalizados según necesidades del equipo',
        'Capacitación en herramientas modernas (Databricks, Spark, Airflow)',
        'SQL avanzado para Data Engineers',
        'Best practices de Data Engineering',
        'Workshops prácticos con casos reales',
        'Seguimiento y certificación del equipo',
        'Modalidad presencial u online',
    ];

    return (
        <>
            <SEO
                title="Soluciones para Empresas - Consultoría en Datos e IA"
                description="Consultoría en datos e inteligencia artificial para empresas. Capacitación para equipos completos en Data Engineering. Soluciones escalables y personalizadas."
                keywords="consultoría data engineering, capacitación equipos, data engineering empresas, IA empresas, big data consultoría"
                url="/empresas"
            />
            <div className="min-h-screen bg-black">
            {/* Header */}
            <div className="relative bg-black text-white py-16">
                <MatrixBackground color="#00ff41" />
                <div className="absolute inset-0 bg-black/80 z-[1]"></div>
                <div className="relative z-10 max-w-6xl mx-auto px-6">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/')}
                        className="text-green-400 hover:bg-green-500/20 mb-6 border border-green-500/50"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Volver al inicio
                    </Button>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center"
                    >
                        <Building2 className="w-16 h-16 mx-auto mb-4 text-green-400" />
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-green-400">
                            Soluciones para Empresas
                        </h1>
                        <p className="text-xl text-white/90 max-w-2xl mx-auto">
                            Transformá tu empresa con consultoría en datos e IA y capacitaciones para equipos completos
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Consultoría Section */}
            <section className="relative py-16 bg-black">
                <MatrixBackground color="#00ff41" />
                <div className="absolute inset-0 bg-black/80 z-[1]"></div>
                <div className="relative z-10 max-w-6xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <div className="text-center mb-12">
                            <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center border-2 border-green-500">
                                <Brain className="w-10 h-10 text-green-400" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-green-400 mb-4">
                                Consultoría en Datos e IA
                            </h2>
                            <p className="text-lg text-white/90 max-w-3xl mx-auto mb-6">
                                Optimizá tus procesos de negocio con soluciones de datos e inteligencia artificial diseñadas a medida
                            </p>
                            <div className="mb-8 bg-black/90 backdrop-blur-sm p-8 rounded-2xl border-2 border-green-500/50 relative z-10">
                                <h3 className="text-4xl md:text-5xl font-bold text-green-400 mb-3">
                                    SIN DATOS DE CALIDAD NO HAY IA
                                </h3>
                                <p className="text-3xl md:text-4xl font-bold text-red-400">
                                    NO DATA = NO IA
                                </p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-12">
                            <Card className="border-2 border-green-500/50 hover:border-green-500 transition-all duration-300 bg-gray-900/95 backdrop-blur-sm shadow-lg h-full flex flex-col relative z-10">
                                <CardHeader>
                                    <CardTitle className="text-xl font-bold text-green-400 flex items-center gap-2">
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                        Beneficios para tu empresa
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4 flex-grow flex flex-col">
                                    <div className="bg-green-500/20 p-4 rounded-lg border border-green-500/50">
                                        <p className="text-lg font-semibold text-green-300 text-center">
                                            Preparamos tus datos para que puedas usar IA sobre ellos
                                        </p>
                                    </div>
                                    <div className="flex items-start gap-3 flex-grow">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <h3 className="font-semibold text-green-400 mb-2">Organización y orden de los datos</h3>
                                            <p className="text-white/80 text-sm leading-relaxed">
                                                Tu empresa genera información valiosa constantemente, pero sin una estructura clara, esos datos se convierten en un activo inutilizable. Te ayudamos a transformar el caos en orden.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <h3 className="font-semibold text-green-400 mb-2">Mayor eficiencia</h3>
                                            <p className="text-white/80 text-sm">
                                                Automatizá procesos y reducí tiempos de análisis de semanas a horas. Liberá tiempo de tu equipo para tareas estratégicas.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <h3 className="font-semibold text-green-400 mb-2">Decisiones basadas en datos</h3>
                                            <p className="text-white/80 text-sm">
                                                Accedé a insights en tiempo real para tomar decisiones estratégicas informadas que impulsen el crecimiento de tu negocio.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <h3 className="font-semibold text-green-400 mb-2">Escalabilidad</h3>
                                            <p className="text-white/80 text-sm">
                                                Arquitecturas diseñadas para crecer con tu negocio, sin límites ni restricciones técnicas.
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-2 border-green-500/50 hover:border-green-500 transition-all duration-300 bg-gray-900/95 backdrop-blur-sm shadow-lg h-full flex flex-col relative z-10">
                                <CardHeader>
                                    <CardTitle className="text-xl font-bold text-green-400 flex items-center gap-2">
                                        <TrendingUp className="w-6 h-6 text-green-500" />
                                        ¿Qué ofrecemos?
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="flex-grow flex flex-col">
                                    <ul className="space-y-3 flex-grow">
                                        {consultoriaFeatures.map((feature, index) => (
                                            <li key={index} className="flex items-start gap-3">
                                                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                                                <span className="text-green-200 font-medium">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="text-center">
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button
                                    size="lg"
                                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                    onClick={() => window.open('https://mail.google.com/mail/?view=cm&fs=1&to=LucianoAgustinArgolo@gmail.com&su=Consulta sobre Consultoría en Datos e IA', '_blank')}
                                >
                                    <Mail className="mr-2 w-5 h-5" />
                                    Contactar por Gmail
                                </Button>
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-2 border-green-500 text-green-400 hover:bg-green-500/20 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                    onClick={() => window.open('https://www.linkedin.com/in/luciano-argolo/', '_blank')}
                                >
                                    <Linkedin className="mr-2 w-5 h-5" />
                                    Contactar por LinkedIn
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Capacitación Section */}
            <section className="relative py-16 bg-black">
                <MatrixBackground color="#00ff41" />
                <div className="absolute inset-0 bg-black/80 z-[1]"></div>
                <div className="relative z-10 max-w-6xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <div className="text-center mb-12">
                            <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center border-2 border-green-500">
                                <Users className="w-10 h-10 text-green-400" />
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold text-green-400 mb-4">
                                Capacitación para Equipos
                            </h2>
                            <p className="text-lg text-white/90 max-w-3xl mx-auto">
                                Formá a tu equipo completo en las herramientas y metodologías más demandadas del mercado
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-12">
                            <Card className="border-2 border-green-500/50 hover:border-green-500 transition-all duration-300 bg-gray-900/95 backdrop-blur-sm relative z-10">
                                <CardHeader>
                                    <CardTitle className="text-xl font-bold text-green-400 flex items-center gap-2">
                                        <Users className="w-6 h-6 text-green-500" />
                                        ¿Qué incluye?
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-3">
                                        {capacitacionFeatures.map((feature, index) => (
                                            <li key={index} className="flex items-start gap-3">
                                                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                                                <span className="text-green-200 font-medium">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>

                            <Card className="border-2 border-green-500/50 hover:border-green-500 transition-all duration-300 bg-gray-900/95 backdrop-blur-sm relative z-10">
                                <CardHeader>
                                    <CardTitle className="text-xl font-bold text-green-400">
                                        Ventajas para tu equipo
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <h3 className="font-semibold text-green-400 mb-2">Aprendizaje práctico</h3>
                                        <p className="text-white/80 text-sm">
                                            Casos reales y ejercicios aplicables directamente a tus proyectos
                                        </p>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-green-400 mb-2">Flexibilidad</h3>
                                        <p className="text-white/80 text-sm">
                                            Programas adaptados a los horarios y necesidades de tu empresa
                                        </p>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-green-400 mb-2">ROI medible</h3>
                                        <p className="text-white/80 text-sm">
                                            Tu equipo aplica lo aprendido inmediatamente, generando valor desde el día uno
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="text-center">
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button
                                    size="lg"
                                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                    onClick={() => window.open('https://mail.google.com/mail/?view=cm&fs=1&to=LucianoAgustinArgolo@gmail.com&su=Consulta sobre Capacitación para Equipos', '_blank')}
                                >
                                    <Mail className="mr-2 w-5 h-5" />
                                    Contactar por Gmail
                                </Button>
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-2 border-green-500 text-green-400 hover:bg-green-500/20 px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                    onClick={() => window.open('https://www.linkedin.com/in/luciano-argolo/', '_blank')}
                                >
                                    <Linkedin className="mr-2 w-5 h-5" />
                                    Contactar por LinkedIn
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA Final */}
            <section className="relative py-16 bg-black">
                <MatrixBackground color="#00ff41" />
                <div className="absolute inset-0 bg-black/80 z-[1]"></div>
                <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-green-400">
                            ¿Listo para transformar tu empresa?
                        </h2>
                        <p className="text-xl text-white/90 mb-8">
                            Agendá una llamada gratuita y armemos una propuesta a medida
                        </p>
                        <Button
                            size="lg"
                            className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                            onClick={() => window.open('https://calendly.com/lucianoagustinargolo/sesion_informativa', '_blank')}
                        >
                            Agendar llamada gratuita
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </motion.div>
                </div>
            </section>
            </div>
        </>
    );
}

